import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { meQuery } from '../api/me.ts';
import { allLapsingInductionsQuery, allOverdueInvoicesQuery } from '../api/venue-page.ts';
import {
  ACCESS_MODE_LABELS,
  type AccessMode,
  createVenue,
  KIND_LABELS,
  RELATIONSHIP_LABELS,
  setAccessMode,
  setVenueEquipment,
  setMine,
  venueSettingsQuery,
  type VenueInput,
  type VenueListItem,
  type VenueRelationship,
  venuesQuery,
} from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { AddButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { zoneName } from './roster-bars.tsx';

// CB-REQ VEN-01 at v1.951, Phase 1's half: the places gear goes. VEN-01 at v1.971 (venue slice 1,
// part A): each venue's kind, how the company relates to it, whether anyone has surveyed it, and a
// star for the venues you look after; clients' premises and other one-off sites stay out of the list
// unless asked for.
//
// VEN-01 at v1.9104, the operator's List board of 2026-10-26 (docs/designs/venue-page.md §3, V-4):
// search, the relationship as one segmented control with counts, Sites and premises, and Needs
// attention — a venue whose problem line is not empty: an induction lapsing within 30 days, an
// overdue invoice (one tenant-wide read of each, grouped here by venue), or not yet surveyed. What
// crew need there, its stock and its details are on the venue's own page. Venues have no aliases
// yet (Phase 2), so search reads the name and the suburb.

const BILLING = { resale: 'Resale', direct_bill: 'Direct bill', agency: 'Agency' } as const;
const RELATIONSHIPS = Object.keys(RELATIONSHIP_LABELS) as VenueRelationship[];
// The segmented control's own short words; the tag in the row uses the full label.
const SEGMENT_LABELS: Record<VenueRelationship, string> = {
  resident: 'In-house',
  partner: 'Partner',
  visited: 'Visited',
  competitor_held: 'Competitor-held',
};

interface Problems {
  lapsing: number;
  overdue: number;
  notSurveyed: boolean;
}
const hasProblem = (p: Problems) => p.lapsing > 0 || p.overdue > 0 || p.notSurveyed;

export function VenuesScreen() {
  const venues = useQuery(venuesQuery);
  const me = useQuery(meQuery);
  const may = (key: string) => me.data?.capabilities.includes(key) ?? false;
  const tenantZone = me.data?.tenant.defaultTz ?? 'Australia/Melbourne';
  // Each problem is asked for only by someone who may read it; without it, it is left out.
  const lapsing = useQuery({ ...allLapsingInductionsQuery, enabled: may('crew.view') });
  const overdue = useQuery({ ...allOverdueInvoicesQuery, enabled: may('invoices.view') });
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [relationship, setRelationship] = useState<VenueRelationship | 'all'>('all');
  const [sites, setSites] = useState(false);
  const [attention, setAttention] = useState(false);

  const problemsOf = (v: VenueListItem): Problems => ({
    lapsing: lapsing.data?.filter((i) => i.venue.id === v.id).length ?? 0,
    overdue: overdue.data?.filter((i) => i.venueId === v.id).length ?? 0,
    notSurveyed: !v.surveyed,
  });
  const words = search.trim().toLowerCase();
  const all = venues.data ?? [];
  const listed = all.filter((v) => v.listed);
  // Filtered by everything but the relationship, so its counts read what choosing it would show.
  const base = all
    .filter((v) => v.listed || sites)
    .filter(
      (v) =>
        !words ||
        v.name.toLowerCase().includes(words) ||
        (v.suburb ?? '').toLowerCase().includes(words),
    )
    .filter((v) => !attention || hasProblem(problemsOf(v)));
  const shown = base
    .filter((v) => relationship === 'all' || v.relationship === relationship)
    .sort((a, b) => Number(b.mine) - Number(a.mine) || a.name.localeCompare(b.name));
  const count = (r: VenueRelationship | 'all') =>
    base.filter((v) => r === 'all' || v.relationship === r).length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Venues"
        title="Venues"
        meta={
          venues.data
            ? [
                `${listed.length} listed`,
                `${listed.filter((v) => v.mine).length} mine`,
                `${listed.filter((v) => !v.surveyed).length} not surveyed`,
              ].join(' · ')
            : undefined
        }
        actions={
          may('venues.manage') && !adding ? (
            <AddButton noun="Venue" onClick={() => setAdding(true)} />
          ) : null
        }
      />
      {adding ? <VenueForm onDone={() => setAdding(false)} /> : null}
      {venues.isPending ? <p className="text-muted">Loading…</p> : null}
      {venues.isError ? (
        <p role="alert" className="alert">
          {venues.error.message}
        </p>
      ) : null}
      {venues.data?.length === 0 && !adding ? (
        <p className="text-ink-soft">
          No venues yet. Add the places you deliver to, and name one on a quote.
        </p>
      ) : null}
      {all.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            className="input max-w-xs"
            placeholder="Name or suburb"
            aria-label="Search venues"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <div className="max-w-full overflow-x-auto">
            <div className="seg" role="radiogroup" aria-label="Relationship">
              {(['all', ...RELATIONSHIPS] as const).map((r) => (
                <label key={r} className="seg-opt">
                  <input
                    type="radio"
                    name="venue-relationship"
                    checked={relationship === r}
                    onChange={() => setRelationship(r)}
                  />
                  {r === 'all' ? 'All' : SEGMENT_LABELS[r]} {count(r)}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={sites}
              onChange={(e) => setSites(e.currentTarget.checked)}
            />
            Sites and premises
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={attention}
              onChange={(e) => setAttention(e.currentTarget.checked)}
            />
            Needs attention
          </label>
        </div>
      ) : null}
      {all.length > 0 && shown.length === 0 ? (
        <p className="text-sm text-muted">No venues match.</p>
      ) : null}
      {shown.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="data-table">
            <caption className="sr-only">Venues, yours first</caption>
            <thead>
              <tr>
                <th scope="col">
                  <span className="sr-only">My venue</span>
                </th>
                <th scope="col">Venue</th>
                <th scope="col">Relationship</th>
                {may('prices.view') ? <th scope="col">Billing</th> : null}
                <th scope="col">Rooms</th>
                <th scope="col">Problems</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((v) => (
                <VenueRow
                  key={v.id}
                  venue={v}
                  tenantZone={tenantZone}
                  seesBilling={may('prices.view')}
                  problems={problemsOf(v)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {may('venues.manage') ? (
        <details className="flex flex-col gap-2">
          <summary className="cursor-pointer text-sm font-semibold">Settings</summary>
          <AccessModeSetting />
        </details>
      ) : null}
    </div>
  );
}

function VenueRow({
  venue: v,
  tenantZone,
  seesBilling,
  problems,
}: {
  venue: VenueListItem;
  tenantZone: string;
  seesBilling: boolean;
  problems: Problems;
}) {
  const queryClient = useQueryClient();
  const star = useMutation({
    mutationFn: () => setMine(v.id, !v.mine),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: venuesQuery.queryKey }),
  });
  const where = [
    v.suburb,
    v.kind === 'venue' ? null : KIND_LABELS[v.kind],
    v.timeZone && v.timeZone !== tenantZone ? zoneName(v.timeZone) : null,
  ].filter(Boolean);
  const margin = v.billing?.hireMarginBp;
  return (
    <tr>
      <td>
        <button
          type="button"
          className="text-lg leading-none text-accent-600 focus-visible:outline-2"
          aria-pressed={v.mine}
          aria-label={`My venue: ${v.name}`}
          title="My venue"
          disabled={star.isPending}
          onClick={() => star.mutate()}
        >
          <span aria-hidden="true">{v.mine ? '★' : '☆'}</span>
        </button>
      </td>
      <th scope="row" className="font-semibold">
        <Link to="/venues/$venueId" params={{ venueId: v.id }} className="link">
          {v.name}
        </Link>
        {where.length > 0 ? (
          <span className="block text-xs font-normal text-muted">{where.join(' · ')}</span>
        ) : null}
      </th>
      <td>
        <span className="tag">{RELATIONSHIP_LABELS[v.relationship]}</span>
      </td>
      {seesBilling ? (
        <td className="text-sm">
          {v.billing
            ? `${BILLING[v.billing.supplyModel]}${
                v.billing.supplyModel === 'direct_bill' && margin != null
                  ? ` · ${margin / 100}%`
                  : ''
              }`
            : '—'}
        </td>
      ) : null}
      <td className="text-sm">
        {v.roomsCount === 0 ? '—' : v.kind === 'venue' ? v.roomsCount : `${v.roomsCount} areas`}
      </td>
      <td>
        <span className="flex flex-wrap gap-1">
          {problems.lapsing > 0 ? (
            <span className="tag tag-accent-2">
              {problems.lapsing === 1 ? 'Induction lapses' : `${problems.lapsing} inductions lapse`}
            </span>
          ) : null}
          {problems.overdue > 0 ? (
            <span className="tag tag-accent-2">
              {problems.overdue === 1 ? 'Invoice overdue' : `${problems.overdue} invoices overdue`}
            </span>
          ) : null}
          {problems.notSurveyed ? <span className="tag tag-outline">Not surveyed</span> : null}
        </span>
      </td>
    </tr>
  );
}

/** A new venue; the venue's own page edits it. */
export function VenueForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [holdsStock, setHoldsStock] = useState(false);
  const [inductionName, setInductionName] = useState('');
  const [inductionMonths, setInductionMonths] = useState('');
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (input: VenueInput) => createVenue(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: venuesQuery.queryKey });
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    const months = Number(inductionMonths);
    if (inductionName.trim() && !(Number.isInteger(months) && months >= 1 && months <= 120)) {
      return setProblem('Say how many months the induction lasts (1 to 120).');
    }
    setProblem(undefined);
    save.mutate({
      name: name.trim(),
      address: address.trim() || null,
      holdsStock,
      induction: inductionName.trim() ? { name: inductionName.trim(), validMonths: months } : null,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4 border-2 border-divider p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Field
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.currentTarget.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={holdsStock}
          onChange={(e) => setHoldsStock(e.currentTarget.checked)}
        />
        Stock on site
      </label>
      {/* CMP-05 / VEN-05 at v1.965: leave the name empty if the venue has no induction. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_10rem]">
        <Field
          label="Induction crew need here"
          hint="Leave empty if there isn’t one."
          value={inductionName}
          onChange={(e) => setInductionName(e.currentTarget.value)}
        />
        <Field
          label="Lasts (months)"
          inputMode="numeric"
          value={inductionMonths}
          onChange={(e) => setInductionMonths(e.currentTarget.value)}
        />
      </div>
      {problem || save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Add venue
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** VEN-02 at v1.971: how the company records access on every venue — a company setting. */
function AccessModeSetting() {
  const settings = useQuery(venueSettingsQuery);
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: setAccessMode,
    onSuccess: (data) => queryClient.setQueryData(venueSettingsQuery.queryKey, data),
  });
  if (!settings.data) return null;
  return (
    <div className="flex max-w-md flex-col gap-1.5">
      <label htmlFor="access-mode" className="text-xs text-ink-soft">
        Access fields
      </label>
      <select
        id="access-mode"
        className="input"
        value={settings.data.mode}
        disabled={change.isPending}
        onChange={(e) => change.mutate(e.currentTarget.value as AccessMode)}
      >
        {(Object.keys(ACCESS_MODE_LABELS) as AccessMode[]).map((m) => (
          <option key={m} value={m}>
            {ACCESS_MODE_LABELS[m]}
          </option>
        ))}
      </select>
      {change.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {change.error.message}
        </p>
      ) : null}
      <VenueEquipmentSetting on={settings.data.venueEquipment} />
    </div>
  );
}

/**
 * INV-A14 at v1.982: whether the company records the equipment venues own, in Inventory beside its
 * own. Off, nothing new can be recorded as a venue's; what was recorded stays.
 */
function VenueEquipmentSetting({ on }: { on: boolean }) {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: setVenueEquipment,
    onSuccess: (data) => queryClient.setQueryData(venueSettingsQuery.queryKey, data),
  });
  return (
    <>
      <label className="mt-2 flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={on}
          disabled={change.isPending}
          onChange={(e) => change.mutate(e.currentTarget.checked)}
        />
        Track venue equipment
      </label>
      {change.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {change.error.message}
        </p>
      ) : null}
    </>
  );
}
